import { Component, OnInit, ViewChild } from '@angular/core';
import { first } from 'rxjs/operators';
import { Incident } from '../model/incident.model';
import { IncidentsService } from '../services/incidents.service';
import ArrayStore from 'devextreme/data/array_store';
import { Division } from '../model/division.model';
import { NotificationService } from '../services/notification.service';
import { DxScrollViewComponent, DxValidationGroupComponent } from 'devextreme-angular';
import { DamageTypesModel } from '../model/damage-types.model';
import { DamageReasonsModel } from '../model/damage-reasons.model';
import { TranslateService } from '@ngx-translate/core';
import { _ } from '@biesbjerg/ngx-translate-extract/dist/utils/utils';
import { Address } from '../model/address.model';
import { Constants } from '../constants/constants';
import { ReportsService } from '../services/reports.service';
import { ReferencesService } from '../services/references.service';
import { HeaderType } from '../model/header-type.enum';
import { ReferenceHelper } from '../model/reference-helper.model';
import { HelperService } from '../services/helper.service';

@Component({
  selector: 'app-incident',
  templateUrl: './incident.component.html',
  styleUrls: ['./incident.component.scss']
})
export class IncidentComponent implements OnInit {

  constructor(private incidentsService: IncidentsService,
              private reportsService: ReportsService,
              private referencesService: ReferencesService,
              private notificationService: NotificationService,
              private translateService: TranslateService,
              private helperService: HelperService) {
  }

  @ViewChild('validationGroupReporterName', { static: false })
  validationGroupReporterName: DxValidationGroupComponent;

  @ViewChild('validationGroupReporterSurname', { static: false })
  validationGroupReporterSurname: DxValidationGroupComponent;

  @ViewChild('scrollView', { static: false })
  scrollView: DxScrollViewComponent;

  title;
  isVisible = false;
  divisions: ArrayStore<Division> = new ArrayStore<Division>();
  damageTypes: ArrayStore<DamageTypesModel> = new ArrayStore<DamageTypesModel>();
  damageReasons: ArrayStore<DamageReasonsModel> = new ArrayStore<DamageReasonsModel>();
  incident: Incident = {
    ArrivalTime: '',
    CivilEngineerArrival: '',
    CivilEngineerInformed: '',
    CivilEngineerName: '',
    Classification: '',
    CreatedDate: '',
    DamageCode: '',
    DamageCodeDescription: '',
    DamageReasonCode: '',
    DamageReasonCodeDescription: '',
    DepartureTime: '',
    Division: '',
    ESTMID: '',
    Forwarded: '',
    HasGasDetected: true,
    HasReferences: false,
    IncidentCity: '',
    IncidentCityPart: '',
    IncidentHouseNo: '',
    IncidentKey: '',
    IncidentName: '',
    IncidentPhone: '',
    IncidentPostalCode: '',
    IncidentRemarks: '',
    IncidentState: '',
    IncidentStreet: '',
    IncidentSurname: '',
    InitialSafeguardingMeasures: false,
    IsEmergency: false,
    Location: '',
    ProcessOwner: '',
    ProcessStarted: '',
    ReferencedRecords: [],
    ReporterCity: '',
    ReporterCityPart: '',
    ReporterHouseNo: '',
    ReporterName: '',
    ReporterPhone: '',
    ReporterPostalCode: '',
    ReporterRemarks: '',
    ReporterStreet: '',
    ReporterSurname: '',
    ResponseGroup: '',
    STMID: '',
    Source: '',
    Type: 0,
    UserID: '',
    UserName: ''
  };
  reporterPostalCodes: string[];
  reporterCities: string[];
  reporterStreets: string[];
  reporterHouseNos: string[];
  reporterCityParts: string[];
  incidentPostalCodes: string[];
  incidentCities: string[];
  incidentStreets: string[];
  incidentHouseNos: string[];
  incidentCityParts: string[];
  locations: string[];
  sources: string[] = Constants.SOURCES;
  classifications: string[] = Constants.CLASSIFICATIONS;
  incidentStates: string[] = Constants.INCIDENT_STATES;

  private isCreateRequested = false;
  private isBindToReport = false;
  private addresses: Address[];
  private rowKey: string;
  private reportSTMID: string;
  private reportRowKey: string;
  private valueMappingConfiguration: Map<string, string> = new Map([
    ['ReporterName', 'IncidentName'],
    ['ReporterSurname', 'IncidentSurname'],
    ['ReporterPhone', 'IncidentPhone'],
    ['ReporterCity', 'IncidentCity'],
    ['ReporterStreet', 'IncidentStreet'],
    ['ReporterPostalCode', 'IncidentPostalCode'],
    ['ReporterHouseNo', 'IncidentHouseNo'],
    ['ReporterCityPart', 'IncidentCityPart']
  ]);

  private static updateIncidentWithReportData(incident: Partial<Incident>, report: Partial<Incident>): void {
    incident.Division = report.Division;
    incident.Source = report.Source;
    incident.CreatedDate = report.CreatedDate;
    incident.HasGasDetected = report.HasGasDetected;
    incident.UserID = report.UserID;
    incident.IsEmergency = report.IsEmergency;
    incident.ReporterName = report.ReporterName;
    incident.ReporterSurname = report.ReporterSurname;
    incident.ReporterPhone = report.ReporterPhone;
    incident.ReporterPostalCode = report.ReporterPostalCode;
    incident.ReporterCity = report.ReporterCity;
    incident.ReporterCityPart = report.ReporterCityPart;
    incident.ReporterStreet = report.ReporterStreet;
    incident.ReporterHouseNo = report.ReporterHouseNo;
    incident.ReporterRemarks = report.ReporterRemarks;
    incident.IncidentName = report.IncidentName;
    incident.IncidentSurname = report.IncidentSurname;
    incident.IncidentPhone = report.IncidentPhone;
    incident.IncidentPostalCode = report.IncidentPostalCode;
    incident.IncidentCity = report.IncidentCity;
    incident.IncidentCityPart = report.IncidentCityPart;
    incident.IncidentStreet = report.IncidentStreet;
    incident.IncidentHouseNo = report.IncidentHouseNo;
    incident.IncidentRemarks = report.IncidentRemarks;
    incident.Location = report.Location;
  }

  ngOnInit(): void {
    this.getDivisions();
    this.getAddresses();
    this.getCreateModeNotification();
    this.getCreateFromReportModeNotification();
    this.getEditModeNotification();
    this.getEditIncidentFromReportModeNotification();
    this.getRefreshIncidentFormNotification();
  }

  getContentHeight(): string {
    return 'calc(80vh - 160px)';
  }

  onReporterPostalCodeChanged(e: any): void {
    setTimeout(() => {
      if (e.value !== null && e.value !== '') {
        const filteredAddresses = this.addresses.filter(x => x.ZIP === e.value);
        this.reporterCities = filteredAddresses.map(c => c.City);
        this.reporterCityParts = filteredAddresses.map(c => c.CityPart);
        this.reporterStreets = filteredAddresses.map(c => c.Street);
      } else {
        this.reporterCities = this.addresses.map(c => c.City);
        this.reporterCityParts = this.addresses.map(c => c.CityPart);
        this.reporterStreets = this.addresses.map(c => c.Street);
      }
    }, 0);
  }

  onReporterCityChanged(e: any): void {
    setTimeout(() => {
      if (e.value !== null && e.value !== '') {
        const filteredAddresses = this.addresses.filter(x => x.City === e.value);
        this.reporterPostalCodes = filteredAddresses.map(c => c.ZIP);
        this.reporterCityParts = filteredAddresses.map(c => c.CityPart);
        this.reporterStreets = filteredAddresses.map(c => c.Street);
      } else {
        this.reporterPostalCodes = this.addresses.map(c => c.ZIP);
        this.reporterCityParts = this.addresses.map(c => c.CityPart);
        this.reporterStreets = this.addresses.map(c => c.Street);
      }
    }, 0);
  }

  onReporterStreetChanged(e: any): void {
    setTimeout(() => {
      if (e.value !== null && e.value !== '') {
        const filteredAddresses = this.addresses.filter(x => x.Street === e.value);
        this.reporterPostalCodes = filteredAddresses.map(c => c.ZIP);
        this.reporterCityParts = filteredAddresses.map(c => c.CityPart);
        this.reporterCities = filteredAddresses.map(c => c.City);
      } else {
        this.reporterPostalCodes = this.addresses.map(c => c.ZIP);
        this.reporterCityParts = this.addresses.map(c => c.CityPart);
        this.reporterCities = this.addresses.map(c => c.City);
      }
    }, 0);
  }

  onIncidentPostalCodeChanged(e: any): void {
    setTimeout(() => {
      if (e.value !== null && e.value !== '') {
        const filteredAddresses = this.addresses.filter(x => x.ZIP === e.value);
        this.incidentCities = filteredAddresses.map(c => c.City);
        this.incidentCityParts = filteredAddresses.map(c => c.CityPart);
        this.incidentStreets = filteredAddresses.map(c => c.Street);
      } else {
        this.incidentCities = this.addresses.map(c => c.City);
        this.incidentCityParts = this.addresses.map(c => c.CityPart);
        this.incidentStreets = this.addresses.map(c => c.Street);
      }
    }, 0);
  }

  onIncidentCityChanged(e: any): void {
    setTimeout(() => {
      if (e.value !== null && e.value !== '') {
        const filteredAddresses = this.addresses.filter(x => x.City === e.value);
        this.incidentPostalCodes = filteredAddresses.map(c => c.ZIP);
        this.incidentCityParts = filteredAddresses.map(c => c.CityPart);
        this.incidentStreets = filteredAddresses.map(c => c.Street);
      } else {
        this.incidentPostalCodes = this.addresses.map(c => c.ZIP);
        this.incidentCityParts = this.addresses.map(c => c.CityPart);
        this.incidentStreets = this.addresses.map(c => c.Street);
      }
    }, 0);
  }

  onIncidentStreetChanged(e: any): void {
    setTimeout(() => {
      if (e.value !== null && e.value !== '') {
        const filteredAddresses = this.addresses.filter(x => x.Street === e.value);
        this.incidentPostalCodes = filteredAddresses.map(c => c.ZIP);
        this.incidentCityParts = filteredAddresses.map(c => c.CityPart);
        this.incidentCities = filteredAddresses.map(c => c.City);
      } else {
        this.incidentPostalCodes = this.addresses.map(c => c.ZIP);
        this.incidentCityParts = this.addresses.map(c => c.CityPart);
        this.incidentCities = this.addresses.map(c => c.City);
      }
    }, 0);
  }

  submitIncident(e: SubmitEvent): void {
    const validReporterName = this.validationGroupReporterName.instance.validate();
    const validReporterSurname = this.validationGroupReporterSurname.instance.validate();
    if (validReporterName.isValid && validReporterSurname.isValid) {
      let success = null;
      this.incidentsService.saveIncident(this.incident).pipe(first()).subscribe(result => {
        success = result !== undefined;
        if (success) {
          this.notificationService.showSuccessMessage(this.translateService.instant(_('Incident successfully saved!')));
          this.popupHiding();
          if (this.isBindToReport) {
            if (!this.isCreateRequested) {
              this.reportsService.editReportBySTMIDNotify(this.reportSTMID);
            } else {
              this.reportsService.createReference(this.reportRowKey, result.IncidentKey).pipe(first()).subscribe(isSuccess => {
                if (!isSuccess) {
                  this.notificationService.showErrorMessage('Incident cannot be referenced to the Report!');
                  return;
                }
                this.notificationService.showSuccessMessage('Incident successfully referenced to the Report!');
                this.reportsService.editReportBySTMIDNotify(this.reportSTMID);
              });
            }
          }
          this.incidentsService.refreshGridNotify();
        } else {
          this.notificationService.showErrorMessage('Incident was not saved.');
        }
      });
    }
    e.preventDefault();
  }

  popupHiding(): void {
    this.isVisible = false;
    this.scrollView.instance.scrollTo(0);
  }

  cancelRequest(): void {
    if (!this.isVisible) {
      return;
    }
    if (this.isCreateRequested) {
      this.incidentsService.cancelIncident(this.incident.STMID).pipe(first()).subscribe(resp => {
        if (!resp) {
          this.notificationService.showErrorMessage('Could not delete incident on Cancel request.');
        }
      });
    }
    this.popupHiding();
  }

  setTodayButton(e: any): void {
    e.component._popup.option('toolbarItems[0]', {
      widget: 'dxButton',
      toolbar: 'bottom',
      location: 'before',
      options: {
        text: 'Today',
        onClick: (x) => {
          e.component.option('value', new Date());
        }
      }
    });
  }

  referenceReport(): void {
    const referenceHelper: ReferenceHelper = {
      ParentRowKey: this.incident.IncidentKey,
      HeaderType: HeaderType.INCIDENTS,
      Division: this.incident.Division
    };
    this.referencesService.referenceNotify(referenceHelper);
  }

  private createIncident(division: number, report: Partial<Incident> = null): void {
    this.incidentsService.createIncident(division).pipe(first()).subscribe(incidentData => {
      this.incident = this.helperService.getValueCopyingIncidentProxy(incidentData, this.valueMappingConfiguration);
      if (this.isBindToReport && report !== null) {
        IncidentComponent.updateIncidentWithReportData(this.incident, report);
      }
      this.getLocations(division);
    });
  }

  private editIncident(): void {
    this.incidentsService.editIncident(this.rowKey).pipe(first()).subscribe(incidentData => {
      this.incident = this.helperService.getValueCopyingIncidentProxy(incidentData, this.valueMappingConfiguration);
      const assignedDivision: number = this.getDivisionID(this.incident.Division);
      if (assignedDivision !== null) {
        this.getLocations(assignedDivision);
        this.getDamageTypes(assignedDivision);
        this.getDamageReasons(assignedDivision);
      }
    });
  }

  private getDivisionID(division: string): number {
    let id = null;
    this.divisions.byKey(division).then(
      (dataItem) => {
        id = Number(dataItem.ID);
      },
      (error) => {
        this.notificationService.showErrorMessage(error);
      }
    );
    return id;
  }

  private getCreateModeNotification(): void {
    this.incidentsService.getCreateModeNotification().subscribe(division => {
      this.title = this.translateService.instant(_('Create Incident'));
      this.getDamageTypes(division);
      this.getDamageReasons(division);
      this.createIncident(division);
      this.isBindToReport = false;
      this.isCreateRequested = true;
      this.isVisible = true;
    });
  }

  private getCreateFromReportModeNotification(): void {
    this.incidentsService.getCreateFromReportModeNotification().subscribe(report => {
      this.title = this.translateService.instant(_('Create Incident'));
      this.reportSTMID = report.STMID;
      this.reportRowKey = report.IncidentKey;
      this.isBindToReport = true;
      const assignedDivision = this.getDivisionID(report.Division);
      this.getDamageTypes(assignedDivision);
      this.getDamageReasons(assignedDivision);
      this.createIncident(assignedDivision, report);
      this.isCreateRequested = true;
      this.isVisible = true;
    });
  }

  private getEditModeNotification(): void {
    this.incidentsService.getEditModeNotification().subscribe(rowKey => {
      this.title = this.translateService.instant(_('Edit Incident'));
      this.rowKey = rowKey;
      this.editIncident();
      this.isBindToReport = false;
      this.isCreateRequested = false;
      this.isVisible = true;
    });
  }

  private getEditIncidentFromReportModeNotification(): void {
    this.incidentsService.getEditIncidentFromReportModeNotification().subscribe(incidentHelper => {
      this.title = this.translateService.instant(_('Edit Incident'));
      this.rowKey = incidentHelper.RowKey;
      this.reportSTMID = incidentHelper.STMID;
      this.editIncident();
      this.isBindToReport = true;
      this.isCreateRequested = false;
      this.isVisible = true;
    });
  }

  private getDivisions(): void {
    this.incidentsService.getDivisions().pipe(first()).subscribe(divisionsContainer => {
      this.divisions = new ArrayStore({ data: divisionsContainer._Divisions, key: 'Name' });
    });
  }

  private getDamageTypes(division: number): void {
    this.incidentsService.getDamageTypes(division).pipe(first()).subscribe(damageTypesContainer => {
      if (damageTypesContainer != null) {
        this.damageTypes = new ArrayStore({ data: damageTypesContainer._DamageTypes, key: 'ID' });
      }
    });
  }

  private getDamageReasons(division: number): void {
    this.incidentsService.getDamageReasons(division).pipe(first()).subscribe(damageReasonsContainer => {
      if (damageReasonsContainer != null) {
        this.damageReasons = new ArrayStore({ data: damageReasonsContainer._DamageReasons, key: 'ID' });
      }
    });
  }

  private getAddresses(): void {
    this.incidentsService.getAddresses().pipe(first()).subscribe(addressesContainer => {
      this.addresses = addressesContainer._Addresses;
      const postalCodes = this.addresses.map((a) => a.ZIP);
      const cities = this.addresses.map((a) => a.City);
      const cityParts = this.addresses.map((a) => a.CityPart);
      const streets = this.addresses.map((a) => a.Street);
      const houseNos = this.addresses.map((a) => a.HouseNo);
      this.reporterPostalCodes = postalCodes;
      this.reporterCities = cities;
      this.reporterCityParts = cityParts;
      this.reporterStreets = streets;
      this.reporterHouseNos = houseNos;
      this.incidentPostalCodes = postalCodes;
      this.incidentCities = cities;
      this.incidentCityParts = cityParts;
      this.incidentStreets = streets;
      this.incidentHouseNos = houseNos;
    });
  }

  private getLocations(division: number): void {
    this.incidentsService.getLocations(division).pipe(first()).subscribe(locationsContainer => {
      if (locationsContainer != null) {
        this.locations = locationsContainer.Locations.map((l) => l.Name);
      }
    });
  }

  private getRefreshIncidentFormNotification(): void {
    this.referencesService.getRefreshIncidentFormNotification().subscribe(success => {
      this.editIncident();
    });
  }
}
